import { useMemo } from 'react';

import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { languageModelsEnpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import useBrains from '@/hooks/useBrains';
import {
  LanguageModel,
  LanguageModels,
  PartialLanguageModel,
  ProviderName,
  Providers,
} from '@/modules/developerTools/types';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

export const API = Object.freeze({
  listLanguageModels: async (): Promise<LanguageModels> => callGet(endpoints.languageModels),

  listProviders: async (): Promise<Providers> => callGet(endpoints.providers),

  createLanguageModel: async (newLanguageModel: Partial<LanguageModel>): Promise<LanguageModel> =>
    callPost(endpoints.languageModels, newLanguageModel),

  getLanguageModel: async (languageModelId: string): Promise<LanguageModel> =>
    callGet(endpoints.languageModel(languageModelId)),

  updateLanguageModel: async (
    languageModelId: string,
    updatedLanguageModel: Partial<LanguageModel>
  ): Promise<LanguageModel> =>
    callPut(endpoints.languageModel(languageModelId), updatedLanguageModel),

  deleteLanguageModel: async (languageModelId: string): Promise<LanguageModel> => {
    const url = `${endpoints.languageModel(languageModelId)}?force=true`;
    return callDelete(url);
  },
});

export const onLanguageModelCreated = (queryClient: QueryClient, languageModel: LanguageModel) => {
  queryClient.setQueryData<LanguageModel>(
    [endpoints.languageModel(languageModel.language_model_id)],
    (prev: LanguageModel) => ({ ...prev, ...languageModel })
  );

  const queryKey = [endpoints.languageModels];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<LanguageModels>(queryKey, (prev) => ({
      language_models: [
        ...(prev?.language_models || []).filter(
          (acc) => acc.language_model_id !== languageModel.language_model_id
        ),
        languageModel,
      ],
    }));
  }
};

export const onLanguageModelDelete = (queryClient: QueryClient, language_model_id: string) => {
  const queryKey = [endpoints.languageModels];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<LanguageModels>(queryKey, (prev: LanguageModels) => ({
      language_models: (prev?.language_models || []).filter(
        (acc) => acc.language_model_id !== language_model_id
      ),
    }));
  }
  queryClient.removeQueries({ queryKey: [endpoints.languageModel(language_model_id)] });
};

export const onLanguageModelUpdated = (queryClient: QueryClient, languageModel: LanguageModel) => {
  const queryKey = [endpoints.languageModel(languageModel.language_model_id)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<LanguageModel>(queryKey, (prev) => ({ ...prev, ...languageModel }));
  }

  // update all
  const modelsQueryKey = [endpoints.languageModels];
  if (queryClient.getQueryData(modelsQueryKey)) {
    queryClient.setQueryData<LanguageModels>(modelsQueryKey, (prev) => ({
      ...prev,
      language_models: prev.language_models.map((m) =>
        m.language_model_id === languageModel.language_model_id ? languageModel : m
      ),
    }));
  }
};
interface Props {
  languageModelId?: string;
  provider?: ProviderName;
}

const useLanguageModels = ({ languageModelId, provider }: Props = {}) => {
  const queryClient = useQueryClient();
  const accountId = useSelector(selectAccountId);
  const { t } = useTranslation();
  const { brains } = useBrains();
  const canReadLanguageModels = useSelector((state: RootState) =>
    getPermissions(state, 'language_models', actions.READ)
  );

  const { data: providers, status: providersStatus } = useQuery<Providers, Error>({
    queryKey: [endpoints.providers],
    queryFn: API.listProviders,
    enabled: !!accountId,
  });

  const { data: languageModels, status: listStatus } = useQuery<LanguageModels, Error>({
    queryKey: [endpoints.languageModels],
    queryFn: () => API.listLanguageModels(),
    enabled: !!accountId && canReadLanguageModels,
  });

  const { mutate: createLanguageModel, status: createStatus } = useMutation<
    LanguageModel,
    Error,
    Partial<LanguageModel>
  >({
    mutationFn: (newLanguageModel: Partial<LanguageModel>) =>
      API.createLanguageModel(newLanguageModel),
    onSuccess: (resp) => {
      onLanguageModelCreated(queryClient, resp);
      toast.success(t('developer_tools.llm.created', { 0: resp.model }));
    },
    meta: { errorToast: true },
  });

  const { data: languageModel, status: getStatus } = useQuery<LanguageModel, Error>({
    queryKey: [endpoints.languageModel(languageModelId)],
    queryFn: () => API.getLanguageModel(languageModelId),
    enabled: !!languageModelId,
  });

  // Update mutation
  const { mutate: updateLanguageModel, status: updateStatus } = useMutation<
    LanguageModel,
    Error,
    PartialLanguageModel
  >({
    mutationFn: ({ language_model_id, ...m }) => API.updateLanguageModel(language_model_id, m),
    onSuccess: (resp) => {
      onLanguageModelUpdated(queryClient, resp);
      toast.success(t('developer_tools.llm.updated', { 0: resp.model }));
    },
    meta: { errorToast: true },
  });

  const { mutate: deleteLanguageModel, status: deleteStatus } = useMutation<
    LanguageModel,
    Error,
    string
  >({
    mutationFn: (languageModelId) => API.deleteLanguageModel(languageModelId),
    onSuccess: (resp) => {
      onLanguageModelDelete(queryClient, resp.language_model_id);
      toast.success(t('developer_tools.llm.deleted', { 0: resp.model }));
    },
  });

  // Get all brains that use the language model
  const languageModelBrains = useMemo(
    () => brains?.filter((brain) => brain.language_model_id === languageModelId),
    [brains, languageModelId]
  );

  // Returns providers having models not created yet
  const uncreatedProviders = useMemo(() => {
    if (!languageModels) {
      return [];
    }

    const createdModels = new Set(
      (languageModels?.language_models || []).map(({ provider, model }) => `${provider}:${model}`)
    );

    return Object.keys(providers || {}).filter(
      (provider) =>
        provider !== 'moveo' &&
        providers[provider].models.some((model) => !createdModels.has(`${provider}:${model.name}`))
    ) as ProviderName[];
  }, [languageModels, providers]);

  const enabledModelsWithMoveo = useMemo(
    () => [
      ...(languageModels?.language_models?.filter((m) => m.enabled) || []),
      {
        provider: 'moveo' as ProviderName,
        language_model_id: 'Default',
        model: 'Default',
        enabled: true,
      },
    ],
    [languageModels]
  );

  // Returns a map of language model ids to their names
  // Default is Moveo
  const languageModelNames = useMemo(() => {
    const result = { Default: 'Moveo' };
    languageModels?.language_models?.forEach((lm) => {
      result[lm.language_model_id] = lm.model;
    });
    return result;
  }, [languageModels]);

  // API keys for the specified provider and current account
  const apiKeys = useMemo(() => {
    if (!languageModels?.language_models || !provider || !accountId) {
      return [];
    }

    const filtered = languageModels.language_models
      .filter((model) => model.provider === provider && model.account_id === accountId)
      .map((model) => ({
        source_language_model_id: model.language_model_id,
        apikey: model.apikey,
      }));

    const seen = new Set();

    return filtered.filter(({ apikey }) => {
      if (seen.has(apikey)) {
        return false;
      }
      seen.add(apikey);
      return true;
    });
  }, [languageModels, provider, accountId]);

  return {
    languageModels: languageModels?.language_models || [],
    listStatus,
    enabledModels: languageModels?.language_models?.filter((m) => m.enabled) || [],
    enabledModelsWithMoveo,
    createLanguageModel,
    createStatus,
    languageModel,
    getStatus,
    updateLanguageModel,
    updateStatus,
    deleteLanguageModel,
    deleteStatus,
    providers,
    providersStatus,
    uncreatedProviders,
    languageModelBrains,
    languageModelNames,
    apiKeys,
  };
};

export default useLanguageModels;
